// ============================================================
// MASH machine_card viewer — shared section chrome (styles + label helpers)
// ============================================================
// Extracted in ME-CARDS-R1 A5 when `machineCardSections.tsx` outgrew the 300-line
// rule. No honesty logic here — styles and label resolution only.
import React from "react";

import type {
  EvidenceTab,
  MachineCardArtifactData,
  Figure,
  SourceRef,
  TurnWindow,
  WindowSelection,
} from "../../../../../../shared/components/machineEvidence";
import {
  formatIsoDateDDMMYYYY,
  guardAgainstGuid,
  isWindowKey,
} from "../../../../../../shared/components/machineEvidence";
import useTranslation from "../../../../../../shared/hooks/useTranslation";
import { dt } from "../../../../../../shared/themes/redesignTokens";
import {
  TURN_WINDOW_TOKEN_LABELS,
  WINDOW_PILL_LABELS,
  tabLabel,
} from "../machineCardConstants";

/** Minimal shape of the i18next `t` these helpers need (its real return type is
 *  not plainly `string`, so call sites cast). */
export type SectionTranslate = (key: string, options?: Record<string, unknown>) => unknown;

export const asText = (value: unknown): string =>
  typeof value === "string" ? value : String(value ?? "");

/** Translate a tab's display label: client six-tab map by slug, falling back to
 *  the tab's own (payload) name for a stray/unknown slug — payload names are
 *  NEVER translated. */
export const useTabLabel = () => {
  const { translate } = useTranslation("machineEvidence");
  return (tab: EvidenceTab): string =>
    translate(`tab.${tab.part_slug}`, { defaultValue: tabLabel(tab) }) as string;
};

export const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
  gap: 12,
};

export const eyebrow: React.CSSProperties = {
  fontSize: 11,
  color: dt.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontFamily: dt.fontMono,
};

export const subtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: dt.textSecondary,
  lineHeight: 1.35,
};

// ───────────────────────────────────────────────────────────────────
// Window labels (ME-CARDS-R1 A2 + A5)
// ───────────────────────────────────────────────────────────────────

export interface TurnPillLabel {
  label: string;
  /** true when the label is the RAW bot token (rendered verbatim, never translated). */
  verbatim: boolean;
}

/** Honest label for the card's turn window: a known token via i18n, else the
 *  formatted window dates, else the raw token VERBATIM, else a neutral chrome
 *  label for "the window this card was built for". */
export const turnWindowLabel = (
  turn: TurnWindow,
  translate: SectionTranslate
): TurnPillLabel => {
  const token = turn.token ?? "";
  if (token && TURN_WINDOW_TOKEN_LABELS[token]) {
    return {
      label: asText(
        translate(`window.turn.${token}`, { defaultValue: TURN_WINDOW_TOKEN_LABELS[token] })
      ),
      verbatim: false,
    };
  }
  const start = formatIsoDateDDMMYYYY(turn.start);
  const end = formatIsoDateDDMMYYYY(turn.end);
  if (start && end && start !== end) return { label: `${start}–${end}`, verbatim: false };
  if (start) return { label: start, verbatim: false };
  if (token) return { label: token, verbatim: true };
  return {
    label: asText(translate("window.turn.thisCard", { defaultValue: "This window" })),
    verbatim: false,
  };
};

/** The label of the CURRENTLY SELECTED window — the pill row and the overview
 *  row tags must always say the same thing. */
export const windowSelectionLabel = (
  selection: WindowSelection,
  turn: TurnWindow,
  translate: SectionTranslate
): string =>
  selection.kind === "turn"
    ? turnWindowLabel(turn, translate).label
    : asText(
        translate(`window.${selection.key}`, { defaultValue: WINDOW_PILL_LABELS[selection.key] })
      );

/** True when the card's turn window needs its own leading pill (i.e. the pinned
 *  5-key row cannot express it). */
export const needsTurnPill = (turn: TurnWindow): boolean => !isWindowKey(turn.token);

// ───────────────────────────────────────────────────────────────────
// Source lookup (A5 — the prototype tile shows its cited signal key)
// ───────────────────────────────────────────────────────────────────

export const sourceMapOf = (card: MachineCardArtifactData): Map<string, SourceRef> => {
  const map = new Map<string, SourceRef>();
  for (const s of card.sources ?? []) map.set(s.id, s);
  return map;
};

/** The GUID-guarded signal key a figure cites, or null. Identity invariant: a
 *  GUID that reached `signal_key` is stripped, never rendered. */
export const figureSignalKey = (
  figure: Figure,
  card: MachineCardArtifactData,
  sources?: Map<string, SourceRef>
): string | null => {
  if (!figure.source_ref) return null;
  const map = sources ?? sourceMapOf(card);
  const src = map.get(figure.source_ref);
  if (!src) return null;
  const guid = card.machine?.machine_id_full || card.machine?.machine || null;
  return guardAgainstGuid(src.signal_key, guid).safe || null;
};
